import { apiHelpers } from './api';

// Types
export interface LeaderboardEntry {
  userId: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  totalXp: number;
  level: number;
  rank: number;
  recentXpGain: number;
  levelTitle: string;
  // Category-specific value (e.g., high score for klobi_jump)
  categoryValue?: number;
  categoryLabel?: string;
}

export interface LeaderboardFilters {
  category?: 'overall' | 'games' | 'events' | 'drinking' | 'klobi_jump';
  timeframe?: 'all' | 'week' | 'month';
  limit?: number;
  offset?: number;
}

export interface LeaderboardResponse {
  entries: LeaderboardEntry[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface UserLeaderboardPosition {
  user: LeaderboardEntry;
  above: LeaderboardEntry[];
  below: LeaderboardEntry[];
  categoryRanks?: CategoryRanks;
}

export interface CategoryRanks {
  games: number | null;
  events: number | null;
  drinking: number | null;
  klobiJump: number | null;
}

// API Functions
export const getLeaderboard = async (
  filters: LeaderboardFilters = {}
): Promise<LeaderboardResponse> => {
  const params = new URLSearchParams();
  if (filters.category) params.append('category', filters.category);
  if (filters.timeframe) params.append('timeframe', filters.timeframe);
  if (filters.limit !== undefined) params.append('limit', filters.limit.toString());
  if (filters.offset !== undefined) params.append('offset', filters.offset.toString());

  const queryString = params.toString();
  return apiHelpers.get<LeaderboardResponse>(
    `/leaderboard${queryString ? `?${queryString}` : ''}`
  );
};

export const getMyPosition = async (): Promise<UserLeaderboardPosition | null> => {
  return apiHelpers.get<UserLeaderboardPosition | null>('/leaderboard/me');
};

export const getUserPosition = async (
  userId: string
): Promise<UserLeaderboardPosition | null> => {
  return apiHelpers.get<UserLeaderboardPosition | null>(`/leaderboard/user/${userId}`);
};

export const getTopUsers = async (
  category: 'overall' | 'games' | 'events' | 'drinking' | 'klobi_jump',
  limit: number = 10
): Promise<LeaderboardEntry[]> => {
  return apiHelpers.get<LeaderboardEntry[]>(
    `/leaderboard/top/${category}?limit=${limit}`
  );
};

// Helper functions
export const getCategoryLabel = (
  category: 'overall' | 'games' | 'events' | 'drinking' | 'klobi_jump'
): string => {
  const labels = {
    overall: 'Gesamt',
    games: 'Spiele',
    events: 'Events',
    drinking: 'Trinkspiele',
    klobi_jump: 'Klobi Jump',
  };
  return labels[category];
};

export const getRankBadge = (rank: number): { emoji: string; color: string } => {
  switch (rank) {
    case 1:
      return { emoji: '🥇', color: 'text-yellow-500' };
    case 2:
      return { emoji: '🥈', color: 'text-gray-400' };
    case 3:
      return { emoji: '🥉', color: 'text-amber-600' };
    default:
      return { emoji: `#${rank}`, color: 'text-muted-foreground' };
  }
};
